import { useState, useEffect } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import TableContainerWithScrollShadow from 'components/TableContainerWithScrollShadow';
import { deleteRecord, errorText, fetchList, ReauthRequiredError } from '../../registryApi';
import { formatDate, orDash } from '../../registryFormat';
import RegistryStatusChip from '../../components/RegistryStatusChip';
import ExperimentAccessDialog from './ExperimentAccessDialog';
import { EXPERIMENT_STATUS } from '../../registryChoices';

// Every registered/draft experiment across all organizations, with its status. Admins open
// an experiment to see its details, and on "By request" experiments use the edit icon in the
// Access column to manage which organizations it's shared with (works after the record locks).
const ExperimentsList = () => {
  const { projectId } = useParams();
  const [experiments, setExperiments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  // Permanent delete (any status), following FileUploader's TemplatesList confirm pattern.
  // The target outlives deleteOpen until the close transition ends, so the text doesn't blank.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const openDelete = (experiment) => {
    setDeleteTarget(experiment);
    setDeleteOpen(true);
  };

  const closeDelete = () => {
    if (deleting) return;
    setDeleteOpen(false);
  };

  const handleDelete = async () => {
    setDeleting(true);
    setDeleteError('');
    try {
      await deleteRecord('/registry/experiments', deleteTarget.id);
      setExperiments((prev) => prev.filter((e) => e.id !== deleteTarget.id));
      setDeleteOpen(false);
    } catch (e) {
      if (e instanceof ReauthRequiredError) {
        window.location.href = e.reauthUrl;
        return;
      }
      setDeleteError(errorText(e, 'Could not delete this experiment.'));
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        setExperiments((await fetchList('/registry/experiments')) || []);
      } catch (e) {
        if (e instanceof ReauthRequiredError) {
          window.location.href = e.reauthUrl;
          return;
        }
        setError(e.message || 'Could not load experiments.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (experiments.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
        No experiments have been registered yet.
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <TableContainerWithScrollShadow>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Registry ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Organization</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Embargo</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {experiments.map((experiment) => (
              <TableRow key={experiment.id} hover>
                <TableCell>
                  <Link
                    component={RouterLink}
                    to={`/projects/${projectId}/registry/${experiment.id}`}
                  >
                    {orDash(experiment.registry_id)}
                  </Link>
                </TableCell>
                <TableCell>{orDash(experiment.experiment_name)}</TableCell>
                <TableCell>{orDash(experiment.registering_organization_name)}</TableCell>
                <TableCell>
                  <RegistryStatusChip status={experiment.status} />
                </TableCell>
                <TableCell>{orDash(formatDate(experiment.embargo_date))}</TableCell>
                <TableCell>{orDash(formatDate(experiment.created_at))}</TableCell>
                {/* Manage access (by-request only) and delete; delete is always last so the
                    icons line up on the right. */}
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {experiment.post_embargo_access === 'by_request' && (
                    <Tooltip title="Manage access">
                      <IconButton
                        size="small"
                        onClick={() => setEditing(experiment)}
                        aria-label={`Manage access for ${experiment.registry_id || 'experiment'}`}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Delete experiment">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => openDelete(experiment)}
                      aria-label={`Delete ${experiment.registry_id || 'experiment'}`}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainerWithScrollShadow>
      <ExperimentAccessDialog
        experiment={editing}
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
      />
      <Dialog
        open={deleteOpen}
        onClose={closeDelete}
        maxWidth="sm"
        fullWidth
        TransitionProps={{
          onExited: () => {
            setDeleteTarget(null);
            setDeleteError('');
          },
        }}
      >
        <DialogTitle>Delete experiment</DialogTitle>
        <DialogContent>
          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deleteError}
            </Alert>
          )}
          {deleteTarget?.status === EXPERIMENT_STATUS.REGISTERED && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This is a registered pre-registration. Other organizations may already have access to it.
            </Alert>
          )}
          <DialogContentText>
            Are you sure you want to permanently delete{' '}
            <strong>
              {deleteTarget?.registry_id || 'this experiment'}
              {deleteTarget?.experiment_name ? ` — ${deleteTarget.experiment_name}` : ''}
            </strong>{' '}
            ({deleteTarget?.registering_organization_name || 'unknown organization'})? Its treatments, outcomes,
            sharing, and uploaded files are deleted too. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDelete} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? <CircularProgress size={20} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExperimentsList;
